import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { format, formatDistanceToNow } from "date-fns";

import RowList from "../../../components/common/rowList";
import Loading from "../../../components/common/loading";
import LoggingEventLogs from "../../logging/eventLogs";
import {
  buildAlertEditPath,
  buildAlertLogsPath,
  buildDeviceAlertsPath,
  getActionParamRows,
  getActionTypeLabel,
  getEventDescription,
  getEventParameterRows,
  getEventRefByCode,
  hydrateEventDescription,
} from "../../../utils/deviceAlertUtils";

import "./deviceAlertDetails.css";

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const toDate = (value, { seconds = false } = {}) => {
  if (!value) return null;
  const date = seconds ? new Date(value * 1000) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatAbsoluteTime = (value, options) => {
  const date = toDate(value, options);
  return date ? format(date, "MMM dd, yyyy HH:mm") : "—";
};

const formatSummaryDate = (value, options) => {
  const date = toDate(value, options);
  return date ? format(date, "MMM dd, yyyy") : "";
};

const formatRelativeTime = (value, options) => {
  const date = toDate(value, options);
  return date ? formatDistanceToNow(date, { addSuffix: true }) : "—";
};

const formatTimeContext = (value, options) => {
  const date = toDate(value, options);
  return date
    ? `${formatAbsoluteTime(date)} (${formatRelativeTime(date)})`
    : "—";
};

const ensureSentence = (value = "") => {
  const trimmedValue = String(value || "").trim();
  if (!trimmedValue) {
    return "";
  }

  return /[.!?]$/.test(trimmedValue) ? trimmedValue : `${trimmedValue}.`;
};

const stripTrailingPunctuation = (value = "") =>
  String(value || "").trim().replace(/[.!?]+$/, "");

const getParameterHelpText = (eventRef, label) =>
  (eventRef?.helpSections || []).find((section) => section.title === label)?.desc || "";

const getEventTypeMeaning = (eventRef) => {
  if (!eventRef) {
    return "This event type monitors the values below and changes state when the configured condition becomes true.";
  }

  const matchingHelpSection = (eventRef.helpSections || []).find(
    (section) => section.title === eventRef.name,
  );

  if (matchingHelpSection?.desc) {
    return matchingHelpSection.desc;
  }

  return `This event type monitors the values below and changes state when the configured ${eventRef.name} condition becomes true.`;
};

const formatLabelList = (labels = []) => {
  if (labels.length === 0) {
    return "";
  }

  if (labels.length === 1) {
    return labels[0];
  }

  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`;
  }

  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
};

const buildHeroDescription = ({
  event,
  currentDescription,
  triggerConditionDescription,
}) => {
  if (!event) return "";

  if (event.triggered) {
    return `This alert is active. ${ensureSentence(
      currentDescription || triggerConditionDescription || "The saved condition is currently true",
    )}`;
  }

  return `This alert is clear right now. It will trigger when ${stripTrailingPunctuation(
    triggerConditionDescription || currentDescription || "the saved condition becomes true",
  )}.`;
};

const buildCurrentStateCopy = ({
  event,
  currentDescription,
}) => {
  if (!event) {
    return "—";
  }

  if (event.triggered) {
    return ensureSentence(
      currentDescription || "The saved condition is currently true",
    );
  }

  return "Clear right now.";
};

const buildSummaryCallout = ({
  event,
  eventTypeLabel,
  deviceName,
  currentDescription,
  triggerConditionDescription,
  actionTypeLabels,
  lastTriggered,
  actionsLoading,
  hasConfiguredActions,
}) => {
  if (!event) {
    return {
      message: "No current details are available for this alert.",
      tone: "neutral",
    };
  }

  const actionList = formatLabelList(actionTypeLabels);
  const actionSentence = actionList
    ? event.triggered
      ? `${actionList} ${actionTypeLabels.length === 1 ? "action was" : "actions were"} issued.`
      : `${actionList} ${actionTypeLabels.length === 1 ? "action will be" : "actions will be"} issued when this alert triggers.`
    : hasConfiguredActions
    ? actionsLoading
      ? ""
      : "Automated actions are attached to this alert."
    : "No automated actions are attached to this alert.";
  const alertContext = `${eventTypeLabel} alert for ${deviceName}`;
  const relativeTriggerTime = lastTriggered
    ? formatRelativeTime(lastTriggered)
    : "";
  const absoluteTriggerDate = lastTriggered
    ? formatSummaryDate(lastTriggered)
    : "";
  const activeDuration = event.triggered && lastTriggered
    ? formatDistanceToNow(lastTriggered)
    : "";

  if (event.triggered) {
    return {
      message: `${alertContext} has been triggered${activeDuration ? ` for ${activeDuration}` : relativeTriggerTime ? ` ${relativeTriggerTime}` : ""}. ${ensureSentence(
        `${stripTrailingPunctuation(
          currentDescription || triggerConditionDescription || "The saved condition is currently true",
        )}${absoluteTriggerDate ? ` at ${absoluteTriggerDate}` : ""}`,
      )}${actionSentence ? ` ${actionSentence}` : ""}`,
      tone: "danger",
    };
  }

  return {
    message: `${alertContext} is clear right now${relativeTriggerTime ? ` and last triggered ${relativeTriggerTime}` : ""}.${actionSentence ? ` ${actionSentence}` : ""}`,
    tone: "neutral",
  };
};

export default function DeviceAlertDetails({
  device,
  events = [],
  eventId,
  deviceOffline,
  loading,
  loaded,
  enclosureSerialNumber,
  alertsPath,
  editPath,
  logsPath,
  canEditEvents,
}) {
  const serial = enclosureSerialNumber || device?.enclosureSerialNumber || "";
  const resolvedDevicePath = serial ? `/devices/connected/${serial}` : "";
  const resolvedAlertsPath = alertsPath || buildDeviceAlertsPath(serial);
  const resolvedEditPath = editPath || buildAlertEditPath(serial, eventId);
  const resolvedLogsPath = logsPath || buildAlertLogsPath(serial, eventId);
  const mayEdit = canEditEvents !== false;

  const [actions, setActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [actionsLoaded, setActionsLoaded] = useState(false);
  const [actionsError, setActionsError] = useState("");

  const event = useMemo(
    () => events.find((existingEvent) => String(existingEvent?.id) === String(eventId)),
    [eventId, events],
  );

  const eventRef = getEventRefByCode(event?.code);
  const currentDescription = event ? getEventDescription(event) : "";
  const triggerConditionDescription = event
    ? hydrateEventDescription(
        eventRef?.triggeredDescription,
        event.params || [],
        event.code,
        eventRef,
      )
    : "";
  const parameterRows = useMemo(() => {
    if (!event) {
      return [];
    }

    return getEventParameterRows(event, device?.apiVersion).map((row) => ({
      ...row,
      helpText: getParameterHelpText(eventRef, row.label),
    }));
  }, [device?.apiVersion, event, eventRef]);

  const selectedActions = useMemo(() => {
    if (!event?.actions?.length) {
      return [];
    }

    return event.actions.map((actionId) => {
      const foundAction = actions.find((action) => action.id == actionId);
      return (
        foundAction || {
          id: actionId,
          name: `Action ${actionId}`,
          code: null,
          params: [],
          missing: true,
        }
      );
    });
  }, [actions, event]);

  useEffect(() => {
    if (!serial || !eventId) return undefined;

    let cancelled = false;
    setActionsLoading(true);
    setActionsLoaded(false);
    setActionsError("");

    axios
      .get(`/v1/actions?enclosure_serial_number=${serial}`, {
        headers: getAuthHeaders(),
      })
      .then((response) => {
        if (cancelled) return;
        setActions(response?.data?.data || []);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to load alert actions", error);
        setActionsError("Unable to load the actions attached to this alert.");
      })
      .finally(() => {
        if (!cancelled) {
          setActionsLoading(false);
          setActionsLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [eventId, serial]);

  if (!event && (!loaded || loading)) {
    return (
      <div className="alert-detail__page">
        <div className="alert-detail__loading">
          <Loading black />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="alert-detail__page">
        <section className="alert-detail__hero alert-detail__hero--empty">
          <div className="alert-detail__hero-copy">
            <div className="alert-detail__eyebrow">Alert Details</div>
            <h3 className="alert-detail__title">Alert not found</h3>
            <p className="alert-detail__description">
              This alert may have been deleted or is no longer available in the
              latest device data.
            </p>
          </div>
          <div className="alert-detail__hero-actions">
            <Link
              to={resolvedAlertsPath}
              className="button secondary"
              style={{ marginBottom: 0 }}
            >
              Back to Alerts
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const deviceName = device?.queuedName || device?.deviceName || "Connected PDU";
  const eventTypeLabel = eventRef?.name || `Event ${event?.code ?? "—"}`;
  const eventTypeMeaning = getEventTypeMeaning(eventRef);
  const hasConfiguredActions = Boolean(event?.actions?.length);
  const isResolvingActions =
    hasConfiguredActions && (!actionsLoaded || actionsLoading);
  const lastTriggered = event?.timeExecutedInEpoch
    ? toDate(event.timeExecutedInEpoch, { seconds: true })
    : null;
  const triggerActionCopy = isResolvingActions
    ? "Loading attached actions..."
    : actionsError && hasConfiguredActions
    ? "Unable to load the attached actions right now."
    : selectedActions.length
    ? `${selectedActions.length} action${
        selectedActions.length === 1 ? "" : "s"
      } ${selectedActions.length === 1 ? "runs" : "run"} automatically when this alert triggers.`
    : "No automated actions are attached to this alert right now.";
  const heroDescription = buildHeroDescription({
    event,
    currentDescription,
    triggerConditionDescription,
  });
  const actionTypeLabels = Array.from(
    new Set(
      selectedActions
        .filter((action) => !action.missing)
        .map((action) => getActionTypeLabel(action.code))
        .filter(Boolean),
    ),
  );
  const currentStateCopy = buildCurrentStateCopy({
    event,
    currentDescription,
  });
  const summaryCallout = buildSummaryCallout({
    event,
    eventTypeLabel,
    deviceName,
    currentDescription,
    triggerConditionDescription,
    actionTypeLabels,
    lastTriggered,
    actionsLoading: isResolvingActions,
    hasConfiguredActions,
  });

  const summaryRows = [
    {
      label: "Status",
      labelStyle: { alignItems: "flex-start" },
      valueStyle: { alignItems: "flex-start" },
      valueComponent: (
        <div className="alert-detail__summary-status">
          <span
            className={`alert-detail__pill ${
              event.triggered
                ? "alert-detail__pill--danger"
                : "alert-detail__pill--success"
            }`}
          >
            {event.triggered ? "Triggered" : "Clear"}
          </span>
          {event?.stale ? (
            <span className="alert-detail__pill alert-detail__pill--warning">
              Stale device state
            </span>
          ) : null}
          {deviceOffline ? (
            <span className="alert-detail__pill alert-detail__pill--warning">
              Device offline
            </span>
          ) : null}
        </div>
      ),
    },
    {
      label: "Current State",
      labelStyle: { alignItems: "flex-start" },
      valueStyle: { alignItems: "flex-start" },
      valueComponent: (
        <div className="alert-detail__row-value-copy">{currentStateCopy}</div>
      ),
    },
    {
      label: "Device",
      value: deviceName,
      link: resolvedDevicePath || undefined,
      valueStyle: { overflowWrap: "anywhere", wordBreak: "break-word" },
    },
    {
      label: "Alert Type",
      value: eventTypeLabel,
    },
    {
      label: "Last Triggered",
      labelStyle: { alignItems: "flex-start" },
      valueStyle: { alignItems: "flex-start" },
      valueComponent: (
        <div className="alert-detail__row-value-copy">
          {lastTriggered
            ? formatTimeContext(lastTriggered)
            : "Not currently triggered"}
        </div>
      ),
    },
    {
      label: "Active Duration",
      value: event.triggered && lastTriggered
        ? formatDistanceToNow(lastTriggered)
        : "Not active",
    },
    {
      label: "Automated Actions",
      labelStyle: { alignItems: "flex-start" },
      valueStyle: { alignItems: "flex-start" },
      valueComponent: (
        <div className="alert-detail__row-value-copy">{triggerActionCopy}</div>
      ),
    },
  ];

  return (
    <div className="alert-detail__page" data-testid="alert-details-page">
      <section className="alert-detail__hero">
        <div className="alert-detail__hero-copy">
          <div className="alert-detail__eyebrow">Alert Details</div>
          <h3 className="alert-detail__title">{event.name}</h3>
          <p className="alert-detail__description">{heroDescription}</p>
          <div className="alert-detail__badge-row">
            <span
              className={`alert-detail__pill ${
                event.triggered
                  ? "alert-detail__pill--danger"
                  : "alert-detail__pill--success"
              }`}
            >
              {event.triggered ? "Triggered" : "Clear"}
            </span>
            {eventRef?.name ? (
              <span className="alert-detail__pill alert-detail__pill--neutral">
                {eventRef.name}
              </span>
            ) : null}
          </div>
        </div>
        <div className="alert-detail__hero-actions">
          <Link
            to={resolvedAlertsPath}
            className="button secondary"
            style={{ marginBottom: 0 }}
          >
            Back to Alerts
          </Link>
        </div>
      </section>

      <div className="alert-detail__grid alert-detail__grid--two-up">
        <section className="alert-detail__card">
          <div className="alert-detail__card-header">
            <div className="alert-detail__card-title-group">
              <h4 className="alert-detail__card-title">Summary</h4>
              <div className="alert-detail__card-copy">
                Current status, timing, device context, and attached automation
                for this alert.
              </div>
            </div>
          </div>
          <div
            className={`alert-detail__summary-callout alert-detail__summary-callout--${summaryCallout.tone}`}
          >
            <div className="alert-detail__summary-callout-message">
              {summaryCallout.message}
            </div>
          </div>
          <div className="alert-detail__summary-rows">
            <RowList rows={summaryRows} />
          </div>
        </section>

        <section className="alert-detail__card">
          <div className="alert-detail__card-header">
            <div className="alert-detail__card-title-group alert-detail__card-title-group--spaced">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h4 className="alert-detail__card-title">Trigger Conditions</h4>
                {mayEdit ? (
                  <div>
                    <Link
                      to={resolvedEditPath}
                      className="alert-detail__link"
                    >
                      Edit Alert
                    </Link>
                  </div>
                ) : null}
              </div>
              <div className="alert-detail__card-copy">
                These saved values tell the device what to monitor, what threshold
                to compare against, and how long the condition must hold before
                the alert changes state.
              </div>
            </div>
          </div>
          <div className="alert-detail__event-type-note">
            <div className="alert-detail__event-type-label">Event Type</div>
            <div className="alert-detail__event-type-title">{eventTypeLabel}</div>
            <div className="alert-detail__event-type-copy">{eventTypeMeaning}</div>
          </div>
          <div className="alert-detail__list">
            {parameterRows.length === 0 ? (
              <div className="alert-detail__empty-note">
                This alert type does not require any extra trigger values.
              </div>
            ) : (
              parameterRows.map((row) => (
                <div key={row.key} className="alert-detail__list-row">
                  <div className="alert-detail__list-main">
                    <div className="alert-detail__list-title">{row.label}</div>
                    {(row.unit || row.helpText) && (
                      <div className="alert-detail__list-meta">
                        {row.unit ? `Unit: ${row.unit}` : ""}
                        {row.unit && row.helpText ? " • " : ""}
                        {row.helpText || ""}
                      </div>
                    )}
                  </div>
                  <div className="alert-detail__list-value">{row.value}</div>
                </div>
              ))
            )}
          </div>
        </section>

      </div>

      <section className="alert-detail__card">
          <div className="alert-detail__card-header">
            <div className="alert-detail__card-title-group">
              <h4 className="alert-detail__card-title">Actions When Triggered</h4>
              <div className="alert-detail__card-copy">
                These actions run automatically when the alert condition is met.
              </div>
            </div>
            {mayEdit ? (
              <Link
                to={resolvedEditPath}
                className="alert-detail__link"
              >
                Edit Alert
              </Link>
            ) : null}
          </div>
          {isResolvingActions ? (
            <div className="alert-detail__loading-inline">
              <Loading black />
            </div>
          ) : actionsError ? (
            <div className="alert-detail__inline-error">{actionsError}</div>
          ) : selectedActions.length === 0 ? (
            <div className="alert-detail__empty-note">
              No actions are currently attached to this alert.
            </div>
          ) : (
            <div className="alert-detail__stack">
              {selectedActions.map((action) => {
                const actionRows = getActionParamRows(action);
                return (
                  <div key={action.id} className="alert-detail__action-card">
                    <div className="alert-detail__action-header">
                      <div>
                        <div className="alert-detail__action-eyebrow">
                          {getActionTypeLabel(action.code)}
                        </div>
                        <div className="alert-detail__action-title">
                          {action.name}
                        </div>
                      </div>
                      {action.missing ? (
                        <span className="alert-detail__pill alert-detail__pill--warning">
                          Unavailable in cloud
                        </span>
                      ) : null}
                    </div>
                    {actionRows.length > 0 ? (
                      <div className="alert-detail__action-params">
                        {actionRows.map((row) => (
                          <div key={row.key} className="alert-detail__action-param">
                            <div className="alert-detail__action-param-label">
                              {row.label}
                            </div>
                            <div className="alert-detail__action-param-value">
                              {row.value || "—"}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="alert-detail__empty-note">
                        No parameters recorded for this action.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
      </section>

      <section className="alert-detail__card alert-detail__card--logs">
        <div className="alert-detail__card-header alert-detail__card-header--logs">
          <div className="alert-detail__card-title-group">
            <h4 className="alert-detail__card-title">History</h4>
            <div className="alert-detail__card-copy">
              Use the timeframe below when you want to focus on a specific period.
            </div>
          </div>
          <Link to={resolvedLogsPath} className="alert-detail__link">
            Open full log page
          </Link>
        </div>
        <LoggingEventLogs
          compactSingleEventView
          embedded
          hideTitle
          hideExport
          hideAlertsOnlyToggle
          hideOperationFilterButton
          fixedEventId={String(eventId)}
          emptyStateText="No alert history was found for this alert."
          device={device}
          match={{ params: { enclosureSerialNumber: serial } }}
          location={{ search: "" }}
          queryParams={{
            enclosure_serial_number: serial,
            eventId: String(eventId),
          }}
          resources={{
            eventLogsPages: [],
            eventLogsMeta: {},
            eventLogsPageLoading: {},
          }}
          fetchResourcePaginate={() => {}}
        />
      </section>
    </div>
  );
}
